
import { Category } from '@/types/assessment';
import { PersonalInfo, AssessmentStep } from './types/progressTypes';

// Calculate total questions across all categories
export function calculateTotalQuestions(categories: Category[]): number {
  return categories.reduce((sum, category) => sum + category.questions.length, 0);
}

// Calculate completed questions count up to a certain category and question
export function calculateCompletedQuestionsCount(
  categories: Category[],
  currentCategoryIndex: number,
  currentQuestionIndex: number
): number {
  return categories.slice(0, currentCategoryIndex).reduce(
    (sum, category) => sum + category.questions.length, 0
  ) + (currentQuestionIndex + 1);
}

// Calculate navigation progress percentage
export function calculateNavigationProgressPercentage(
  totalQuestions: number,
  completedQuestionsCount: number
): number {
  return totalQuestions > 0 
    ? Math.min(99, Math.round((completedQuestionsCount / totalQuestions) * 100))
    : 0;
}

// Save assessment progress to localStorage
export function saveProgressToLocalStorage(
  assessmentId: string,
  categoryIndex: number,
  questionIndex: number,
  step: AssessmentStep,
  personalInfo: PersonalInfo
): void {
  const savedProgress = localStorage.getItem(`assessment-progress-${assessmentId}`);
  let progressData = {};
  
  if (savedProgress) {
    try {
      progressData = JSON.parse(savedProgress);
    } catch (error) {
      console.error('Error parsing saved progress:', error);
    }
  }
  
  localStorage.setItem(`assessment-progress-${assessmentId}`, JSON.stringify({
    ...progressData,
    categoryIndex,
    questionIndex,
    step,
    personalInfo
  }));
}

// Load assessment progress from localStorage
export function loadProgressFromLocalStorage(assessmentId: string): {
  categoryIndex: number;
  questionIndex: number;
  step: AssessmentStep;
  personalInfo: PersonalInfo;
} {
  const defaultValues = {
    categoryIndex: 0,
    questionIndex: 0,
    step: 'welcome' as AssessmentStep,
    personalInfo: {
      fullName: '',
      email: '',
      company: '',
      designation: '',
      consentGiven: false
    }
  };

  const savedProgress = localStorage.getItem(`assessment-progress-${assessmentId}`);
  
  if (!savedProgress) {
    return defaultValues;
  }
  
  try {
    const parsed = JSON.parse(savedProgress);
    return {
      categoryIndex: parsed.categoryIndex || defaultValues.categoryIndex,
      questionIndex: parsed.questionIndex || defaultValues.questionIndex,
      step: parsed.step || defaultValues.step,
      personalInfo: parsed.personalInfo || defaultValues.personalInfo
    };
  } catch (error) {
    console.error('Error parsing saved progress:', error);
    return defaultValues;
  }
}

// Clear assessment progress from localStorage
export function clearProgressFromLocalStorage(assessmentId: string): void {
  localStorage.removeItem(`assessment-progress-${assessmentId}`);
}
